import * as React from "react";

import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { IcChevronDownThin } from "@racoon/icons";
import { getEnumName } from "@racoon/lib";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  StatusPill,
} from "@racoon/ui";

import { jobStatusTone } from "~/features/map-view/model/constants/job-status-colors";
import { enumNumber } from "../model/job-enums";
import { OUTCOMES, outcomeOf, type Outcome } from "../model/outcome-correction";

type Props = {
  job: ConnectProto.Job;
  /** The outcome picked but not saved yet; a verdict press saves it. */
  staged?: Outcome;
  onStage: (outcome: Outcome) => void;
  disabled?: boolean;
};

// One width for every status, so the pill keeps its size as the job moves on (Figma: 118)
const PILL_WIDTH = "w-[118px]";

const statusKey = (status?: ConnectProto.JobStatus) =>
  getEnumName(ConnectProto.JobStatusSchema, status);

const toneOf = (status: ConnectProto.JobStatus) => {
  const tone = jobStatusTone(status);
  return tone && `${tone.bg} ${tone.color}`;
};

/** The job's status — a dropdown once it ended, to correct its outcome; a pill before. */
export const JobStatusSelect: React.FC<Props> = ({ job, staged, onStage, disabled }) => {
  const t = useT();
  const outcome = outcomeOf(job.status);

  if (outcome === undefined) {
    // The job map hands its jobs over with enum names
    const status = enumNumber(ConnectProto.JobStatusSchema, job.status);
    const key = statusKey(status);
    const tone = status !== undefined && key && toneOf(status);
    if (!key || !tone) return null;
    return (
      <StatusPill tone={tone} className={`${PILL_WIDTH} self-start`}>
        {t(Namespace.Enum, key)}
      </StatusPill>
    );
  }

  const shown = staged ?? outcome;
  const shownKey = statusKey(shown)!;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="job-status-select"
        disabled={disabled}
        className="self-start rounded-full disabled:opacity-60"
      >
        <StatusPill tone={toneOf(shown)} density="tight" className={PILL_WIDTH}>
          <span className="truncate">{t(Namespace.Enum, shownKey)}</span>
          {/* Pushed right, 6px from the edge: auto margins leave the pill's own padding be */}
          <IcChevronDownThin className="-mr-1.5 ml-auto size-5 shrink-0" />
        </StatusPill>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup
          value={String(shown)}
          onValueChange={(value) => {
            const next = outcomeOf(Number(value));
            if (next !== undefined) onStage(next);
          }}
        >
          {OUTCOMES.map((option) => (
            <DropdownMenuRadioItem
              key={option}
              value={String(option)}
              data-testid={`job-status-option-${statusKey(option)}`}
            >
              {t(Namespace.Enum, statusKey(option)!)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
